import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { transRoutesSlice } from "../../store";
import RouteCard from "./RouteCard";
import { Modal, Button } from "react-bootstrap";
import { AuthService, TransRoutesService, EmployeeService, EventsService, DriverService } from "../../services";
import { buildExternalDriverNameByIdMap } from "../../shared/utils/driverDropdownOptions";
import moment from 'moment';

/** Clear runtime status / times when copying a route (Import Inbound / Import Outbound). */
const stripRouteCustomerStatusAndTimes = (customer) => {
  const addr = (customer.customer_address || '').toString().trim();
  const base =
    (customer.customer_route_base_address || '').toString().trim() || addr;
  return Object.assign({}, customer, {
    customer_enter_center_time: null,
    customer_leave_center_time: null,
    customer_pickup_time: null,
    customer_dropoff_time: null,
    customer_estimated_pickup_time: null,
    customer_estimated_dropoff_time: null,
    customer_route_status: null,
    customer_pickup_status: null,
    customer_route_base_address: base || undefined,
    customer_address: base || customer.customer_address,
  });
};

const buildClonedRoutePayload = (sourceRoute, targetRouteType, newName) => {
  const payload = Object.assign({}, sourceRoute, { type: targetRouteType, name: newName });
  delete payload.id;
  delete payload._id;
  if (!payload.route_customer_list) {
    return payload;
  }
  const customers = payload.route_customer_list.slice().sort((a, b) => b.customer_pickup_order - a.customer_pickup_order);
  let n = 0;
  for (let i = 0; i < customers.length - 1; i++) {
    if (i === 0) {
      customers[i] = Object.assign({}, customers[i], { customer_pickup_order_new: n });
    }
    if (customers[i + 1].customer_pickup_order === customers[i].customer_pickup_order) {
      customers[i + 1] = Object.assign({}, customers[i + 1], { customer_pickup_order_new: n });
    } else {
      n++;
      customers[i + 1] = Object.assign({}, customers[i + 1], { customer_pickup_order_new: n });
    }
  }
  const customersPayload = customers.map((customer) => {
    const stripped = stripRouteCustomerStatusAndTimes(customer);
    const newCustomer = {
      ...stripped,
      customer_pickup_order: customer.customer_pickup_order_new,
    };
    delete newCustomer.customer_pickup_order_new;
    return newCustomer;
  });
  return Object.assign({}, payload, { route_customer_list: customersPayload });
};

const RoutesSection = ({
  transRoutes,
  copyList,
  sectionName,
  drivers,
  vehicles,
  canAddNew,
  addText,
  copyText,
  redirect,
  routeType,
  isTemplate,
  templateId,
  showRouteHeaderButtons = false,
  clearAllRoutesAction = null,
  fetchAfterCreate = null,
  importConfirmMessage = '',
  clearConfirmMessage = '',
  /** When set (e.g. daily route template), import modal saves via this instead of Redux createRoute. */
  templateImportSave = null,
  /** For template import: one API round-trip when copying all opposite routes. If unset, `templateImportSave` runs in sequence. */
  templateCopyAllSave = null,
}) => {
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [routesOptions, setRouteOptions] = useState([]);
  const [newRoute, setNewRoute] = useState(undefined);
  const [newName, setNewName] = useState('');
  const [externalDriverNameById, setExternalDriverNameById] = useState({});
  const [allDriverNameById, setAllDriverNameById] = useState({});
  const dispatch = useDispatch();
  const { createRoute } = transRoutesSlice.actions;
  const params = new URLSearchParams(window.location.search);
  const scheduleDate = params.get('dateSchedule');

  const routeSectionKind = (() => {
    if (routeType === 'inbound' || routeType === 'outbound') return routeType;
    const name = `${sectionName || ''}`;
    if (name.includes('Outbound') || name.toLowerCase().includes('outbound')) return 'outbound';
    if (name.includes('Inbound') || name.toLowerCase().includes('inbound')) return 'inbound';
    return 'inbound';
  })();

  const {
    routeCount,
    scheduledTotal,
    checkedInCount,
    checkedOutCount,
    checkedInMembers,
    checkedInVisitors,
    checkedOutMembers,
    checkedOutVisitors,
  } = useMemo(
    () => TransRoutesService.getRouteSectionManifestStats(transRoutes, routeSectionKind),
    [transRoutes, routeSectionKind]
  );

  const routeStatsSummary =
    (sectionName.includes('Inbound') || sectionName.includes('Outbound')) &&
    (routeSectionKind === 'outbound'
      ? `${routeCount} Routes · ${scheduledTotal} Scheduled · ${checkedOutCount} Checked Out (${checkedOutMembers} Members · ${checkedOutVisitors} Visitors)`
      : `${routeCount} Routes · ${scheduledTotal} Scheduled · ${checkedInCount} Checked In (${checkedInMembers} Members · ${checkedInVisitors} Visitors)`);

  const externalDriverLookupKey = useMemo(() => {
    if (!transRoutes?.length) return '';
    return transRoutes.map((r) => r?.driver ?? '').sort().join('|');
  }, [transRoutes]);

  useEffect(() => {
    if (!externalDriverLookupKey) {
      setExternalDriverNameById({});
      return;
    }
    let cancelled = false;
    EmployeeService.getExternalUserPermissionsList(EventsService.site)
      .then((res) => {
        if (!cancelled) setExternalDriverNameById(buildExternalDriverNameByIdMap(res?.data || []));
      })
      .catch(() => {
        if (!cancelled) setExternalDriverNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, [externalDriverLookupKey]);

  useEffect(() => {
    let cancelled = false;
    DriverService.getAllActiveDrivers('driver')
      .then((res) => {
        if (cancelled) return;
        const next = {};
        (res?.data || []).forEach((driverItem) => {
          if (driverItem?.id == null) return;
          const name = `${driverItem?.name || ''}`.trim();
          if (!name) return;
          next[String(driverItem.id)] = name;
        });
        setAllDriverNameById(next);
      })
      .catch(() => {
        if (!cancelled) setAllDriverNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openCopyModal = () => {
    setRouteOptions(copyList);
    setShowCopyModal(true);
  }

  const closeCopyModal = () => {
    setRouteOptions([]);
    setNewRoute(undefined);
    setNewName('');
    setShowCopyModal(false);
  }

  const getFetchParamAfterCreate = () => {
    if (fetchAfterCreate) {
      return fetchAfterCreate;
    }
    if (scheduleDate) {
      return { fetchDate: moment(scheduleDate).format('MM/DD/YYYY') };
    }
    return { fetchTomorrow: true };
  };

  const onSelectExistedRoute = (e) => {
    const value = e.currentTarget.value;
    setNewRoute(value);
    if (!value) {
      setNewName('');
      return;
    }
    const found = (routesOptions || copyList || []).find(
      (item) => String(item.id) === String(value)
    );
    if (found?.name != null) {
      setNewName(found.name);
    }
  };

  const copyAllFromOpposite = () => {
    if (!copyList || copyList.length === 0) {
      return;
    }
    const payloads = copyList.map((sourceRoute) => buildClonedRoutePayload(sourceRoute, routeType, sourceRoute.name));
    closeCopyModal();
    if (templateCopyAllSave) {
      Promise.resolve(templateCopyAllSave(payloads)).catch(() => {
        window.alert('Failed to import routes.');
      });
      return;
    }
    if (templateImportSave) {
      (async () => {
        try {
          for (const finalPayload of payloads) {
            // eslint-disable-next-line no-await-in-loop
            await templateImportSave(finalPayload);
          }
        } catch {
          window.alert('Failed to import routes.');
        }
      })();
      return;
    }
    const fetchParam = getFetchParamAfterCreate();
    for (const finalPayload of payloads) {
      dispatch(createRoute(Object.assign({}, { data: finalPayload }, fetchParam)));
    }
  };

  const saveRoute = () => {
    if (newRoute && newName) {
      const found = copyList.find((item) => String(item.id) === String(newRoute));
      if (!found) {
        return;
      }
      const finalPayload = buildClonedRoutePayload(found, routeType, newName);
      if (templateImportSave) {
        Promise.resolve(templateImportSave(finalPayload))
          .then(() => closeCopyModal())
          .catch(() => {
            window.alert("Failed to import route.");
          });
        return;
      }
      const fetchParam = getFetchParamAfterCreate();
      dispatch(createRoute(Object.assign({}, { data: finalPayload }, fetchParam)));
      closeCopyModal();
    }
  }

  


  return (
    <>
      <div
        className={`text-primary ${canAddNew || copyText || clearAllRoutesAction ? "mb-2" : ""} d-flex align-items-center ${showRouteHeaderButtons ? "justify-content-start" : "justify-content-between"}`}
        style={showRouteHeaderButtons ? { gap: '12px', flexWrap: 'wrap' } : {}}
      >
        <h6 className={`${showRouteHeaderButtons ? 'me-1' : 'me-4'} mb-0`}>
          {`${sectionName}: `}
          <span className="route-stats">{routeStatsSummary}</span>
        </h6>
        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
          {canAddNew && (
            showRouteHeaderButtons ? (
              <button className="btn btn-outline-secondary" onClick={() => { if (routeType) {redirect(routeType)} else {redirect()}}}>
                {addText}
              </button>
            ) : (
              <small className="me-4" onClick={() => { if (routeType) {redirect(routeType)} else {redirect()}}}>
                <a>{addText}</a>
              </small>
            )
          )}
          {copyText && (
            showRouteHeaderButtons ? (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => {
                  if (importConfirmMessage && !window.confirm(importConfirmMessage)) return;
                  openCopyModal();
                }}
              >
                {copyText}
              </button>
            ) : (
              <small
                onClick={() => {
                  if (importConfirmMessage && !window.confirm(importConfirmMessage)) return;
                  openCopyModal();
                }}
              >
                <a>{copyText}</a>
              </small>
            )
          )}
          {clearAllRoutesAction && showRouteHeaderButtons && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => {
                if (clearConfirmMessage && !window.confirm(clearConfirmMessage)) return;
                clearAllRoutesAction();
              }}
            >
              Clear All Routes
            </button>
          )}
        </div>
      </div>
      {
        transRoutes && (<>
          <div className="all-routes-container">
            {
              transRoutes.map((r) => Object.assign({}, r, {vehicleNumber: vehicles?.find((v) => r.vehicle === v.id)?.vehicle_number || 0})).sort((a, b) => a?.vehicleNumber - b?.vehicleNumber).map((route, index) => <div key={route.id} className="route-card-container"><RouteCard routeIndex={index} transRoute={route} drivers={drivers} vehicles={vehicles} isTemplate={isTemplate} templateId={templateId} externalDriverNameById={externalDriverNameById} allDriverNameById={allDriverNameById}/></div>)
            }
          </div>
        </>)
      }
      {
        AuthService.canViewDrivers() && !transRoutes && drivers && (<>
          <div className="list row">
            {
              drivers.map((driver) => <div key={driver.id} className="col-md-3 col-sm-6 col-xs-12"><RouteCard driver={driver}/></div>)
            }
          </div>
        </>)
      }
      {
        AuthService.canViewVechiles() && !transRoutes && vehicles && (<>
          <div className="list row">
            {
              vehicles.map((vehicle) => <div key={vehicle.id} className="col-md-3 col-sm-6 col-xs-12"><RouteCard vehicle={vehicle}/></div>)
            }
          </div>
        </>)
      }
      <Modal show={showCopyModal} onHide={() => closeCopyModal()}>
        <Modal.Header closeButton>
          <Modal.Title>Copy Route</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            <div className="mb-4">Select an Existed Route: <select value={newRoute ?? ''} onChange={onSelectExistedRoute}>
              <option value=""></option>
              {routesOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select></div>
            <div className="mb-2">Type in New Route Name: <input type="text" value={newName} onChange={(e) => { setNewName(e.currentTarget.value); }} /></div>
            {!!copyList?.length && routeType && (
              <div>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={copyAllFromOpposite}
                >
                  {routeType === 'inbound' ? 'Copy all from outbound' : 'Copy all from inbound'}
                </Button>
              </div>
            )}
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => closeCopyModal()}>
            Close
          </Button>
          <Button variant="primary" onClick={() => saveRoute()}>
            Save Route
          </Button>
        </Modal.Footer>
      </Modal>
    </>
    
  );
};

export default RoutesSection;